import { useCallback, useEffect, useRef } from 'react';

/**
 * This hook prevents promises that are made in `useEffect`s from resolving
 * when components are unmounted.
 *
 * @link https://dev.to/rodw1995/cancel-your-promises-when-a-component-unmounts-gkl
 */
function usePromise() {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return useCallback(
    <T>(promise: Promise<T>, onCancel?: () => void) =>
      new Promise<T>((resolve, reject) => {
        promise
          .then(result => {
            if (isMounted.current) resolve(result);
          })
          .catch(error => {
            if (isMounted.current) reject(error);
          })
          .finally(() => {
            if (!isMounted.current && onCancel) onCancel();
          });
      }),
    []
  );
}

export default usePromise;
